import { NoddiIcon } from 'noddi-ui-common';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../../../base/dropdown-menu';
import { NoddiButton } from '../../../Inputs/NoddiButton';
import { NoddiSheet } from '../../../Overlay/NoddiSheet';
import { RowSelectionActionBarTranslations } from '../tableTranslationTypes';

export type RowSelectionAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
};

type RowSelectionActionButtonsProps = {
  actions: RowSelectionAction[];
  translations: RowSelectionActionBarTranslations;
  /** Styles the CTA as a light button for the dark selection bar (vs. the light "top" placement). */
  onDark?: boolean;
};

// White CTA on the dark bar: `--selection-bar` is the bar's (dark) background across all brand themes,
// so a white surface with primary-colored text stays readable everywhere.
const onDarkCtaClassName =
  'border-white bg-white text-selection-bar shadow-[0_1px_6px_rgba(0,0,0,0.22)] md:hover:bg-card-light-background md:hover:text-selection-bar';

export function RowSelectionActionButtons({ actions, translations, onDark }: RowSelectionActionButtonsProps) {
  if (actions.length === 0) {
    return null;
  }

  // Single action: render as direct button
  if (actions.length === 1) {
    const action = actions[0];
    if (!action) {
      return null;
    }

    return (
      <NoddiButton
        size='small'
        className={onDark ? onDarkCtaClassName : undefined}
        disabled={action.disabled}
        loading={action.loading}
        onClick={action.onClick}
      >
        {action.label}
      </NoddiButton>
    );
  }

  // Multiple actions: render as dropdown. Surface action loading on the
  // trigger too — picking a dropdown item closes the menu, so a spinner
  // inside the item alone would never be visible during the request.
  const anyActionLoading = actions.some((action) => action.loading);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <NoddiButton
          variant='primary'
          size='small'
          endIcon='ChevronUp'
          className={onDark ? onDarkCtaClassName : undefined}
          loading={anyActionLoading}
          disabled={anyActionLoading}
        >
          {translations.actions}
        </NoddiButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        {actions.map((action) => (
          <DropdownMenuItem key={action.label} disabled={action.disabled || action.loading} onClick={action.onClick}>
            <div className='flex items-center gap-2'>
              {action.loading ? <NoddiIcon name='Loader2' size='xSmall' className='animate-spin' /> : null}
              <span>{action.label}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type RowSelectionMobileMenuProps = {
  selectedCount: number;
  actions: RowSelectionAction[];
  translations: RowSelectionActionBarTranslations;
  onClearSelection: () => void;
  showSelectAllMatching: boolean;
  onSelectAllMatching?: () => void;
  isSelectAllMatchingLoading?: boolean;
  selectAllMatchingText?: string;
};

function RowSelectionMobileMenu({
  selectedCount,
  actions,
  translations,
  onClearSelection,
  showSelectAllMatching,
  onSelectAllMatching,
  isSelectAllMatchingLoading,
  selectAllMatchingText
}: RowSelectionMobileMenuProps) {
  const [open, setOpen] = useState(false);
  const anyActionLoading = actions.some((action) => action.loading);
  const closeAndRun = (onClick?: () => void) => {
    setOpen(false);
    onClick?.();
  };

  return (
    <>
      <NoddiButton
        variant='primary'
        size='small'
        endIcon='ChevronUp'
        className={onDarkCtaClassName}
        loading={anyActionLoading}
        disabled={anyActionLoading}
        aria-haspopup='dialog'
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        {translations.actions}
      </NoddiButton>
      <NoddiSheet
        open={open}
        onOpenChange={setOpen}
        side='bottom'
        className='rounded-t-xl'
        title={translations.actions}
        description={translations.itemsSelected(selectedCount)}
      >
        <div className='flex flex-col gap-4'>
          {actions.map((action) => (
            <NoddiButton
              key={action.label}
              variant='outline'
              className='w-full justify-start'
              disabled={action.disabled}
              loading={action.loading}
              onClick={() => closeAndRun(action.onClick)}
            >
              {action.label}
            </NoddiButton>
          ))}
          <div className='h-px bg-border' />
          {showSelectAllMatching && (
            <NoddiButton
              variant='ghost'
              className='w-full justify-start px-0 text-muted-foreground'
              startIcon='ListCheck'
              loading={isSelectAllMatchingLoading}
              onClick={() => closeAndRun(onSelectAllMatching)}
            >
              {selectAllMatchingText}
            </NoddiButton>
          )}
          {/* eslint-disable-next-line noddi/overlay-actions-in-footer -- an entry of the actions menu, not a Cancel / Save row */}
          <NoddiButton
            variant='ghost'
            className='w-full justify-start px-0 text-muted-foreground'
            startIcon='X'
            onClick={() => closeAndRun(onClearSelection)}
          >
            {translations.clearSelection}
          </NoddiButton>
        </div>
      </NoddiSheet>
    </>
  );
}

type Props = {
  selectedCount: number;
  totalMatchingCount?: number;
  actions: RowSelectionAction[];
  onClearSelection: () => void;
  onSelectAllMatching?: () => void;
  isSelectAllMatchingLoading?: boolean;
  /** When set, overrides `translations.selectAllMatching` for this control only. */
  selectAllMatchingLabel?: (totalMatchingCount: number) => string;
  /** Optional contextual line under the count, e.g. the first selected row's plate and model, or `"{plate} and N more"`. */
  subtitle?: string;
  // this only makes sense to use inline in the table component where the translations are passed down
  translations: RowSelectionActionBarTranslations;
};

export function RowSelectionActionBar({
  selectedCount,
  totalMatchingCount,
  actions,
  onClearSelection,
  onSelectAllMatching,
  isSelectAllMatchingLoading,
  selectAllMatchingLabel,
  subtitle,
  translations
}: Props) {
  if (selectedCount === 0 || actions.length === 0) {
    return null;
  }

  const showSelectAllMatching =
    onSelectAllMatching &&
    totalMatchingCount !== undefined &&
    totalMatchingCount > 0 &&
    selectedCount < totalMatchingCount;
  const selectAllMatchingText =
    showSelectAllMatching && (selectAllMatchingLabel ?? translations.selectAllMatching)(totalMatchingCount);

  return (
    // shrink-0: the bar sits in the table's flex column next to a flex-1 scroll area. Without it,
    // a height-capped table squeezes the bar to a few pixels instead of shortening the rows.
    <div className='flex h-[54px] w-full shrink-0 animate-slide-up items-center overflow-hidden rounded-md border-t border-white/10 bg-selection-bar bg-[linear-gradient(180deg,_rgba(255,255,255,0.09)_0%,_rgba(255,255,255,0)_100%)] px-4 text-selection-bar-foreground md:px-5'>
      <div className='flex min-w-0 flex-1 items-center justify-between gap-4 md:hidden'>
        <div className='flex min-w-0 flex-col gap-[3px]'>
          <span className='truncate text-sm leading-none font-semibold'>
            {translations.itemsSelected(selectedCount)}
          </span>
          {subtitle && <span className='truncate text-[11px] leading-none text-white/45'>{subtitle}</span>}
        </div>
        <RowSelectionMobileMenu
          selectedCount={selectedCount}
          actions={actions}
          translations={translations}
          onClearSelection={onClearSelection}
          showSelectAllMatching={!!showSelectAllMatching}
          onSelectAllMatching={onSelectAllMatching}
          isSelectAllMatchingLoading={isSelectAllMatchingLoading}
          selectAllMatchingText={selectAllMatchingText || undefined}
        />
      </div>

      <div className='hidden h-full shrink-0 items-center gap-3 border-r border-white/10 pr-5 md:flex'>
        <span
          key={selectedCount}
          className='flex size-7 shrink-0 animate-count-pop items-center justify-center rounded-lg border border-white/20 bg-white/[0.13] text-sm font-bold'
        >
          {selectedCount}
        </span>
        <div className='flex min-w-0 flex-col gap-[3px]'>
          <span className='text-[13px] leading-none font-semibold whitespace-nowrap'>
            {translations.itemsSelected(selectedCount)}
          </span>
          {subtitle && (
            <span className='truncate text-[11px] leading-none tracking-[0.01em] text-white/45'>{subtitle}</span>
          )}
        </div>
      </div>

      {showSelectAllMatching && (
        <button
          type='button'
          disabled={isSelectAllMatchingLoading}
          onClick={onSelectAllMatching}
          className='hidden shrink-0 items-center gap-1.5 px-4 text-xs font-medium whitespace-nowrap text-white/45 transition-colors hover:text-white/70 disabled:opacity-50 md:flex'
        >
          {isSelectAllMatchingLoading && <NoddiIcon name='Loader2' size='xSmall' className='animate-spin' />}
          {selectAllMatchingText}
        </button>
      )}

      <span className='hidden flex-1 md:block' />

      <div className='hidden shrink-0 items-center gap-1.5 md:flex'>
        <button
          type='button'
          onClick={onClearSelection}
          className='flex items-center gap-[5px] rounded-md px-2.5 py-1.5 text-xs font-medium text-white/50 transition-colors hover:text-white/75'
        >
          <NoddiIcon name='X' size='xSmall' />
          {translations.clearSelection}
        </button>
        <div className='h-[22px] w-px shrink-0 bg-white/10' />
        <RowSelectionActionButtons actions={actions} translations={translations} onDark />
      </div>
    </div>
  );
}
